import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBasket, Menu, X, MapPin } from "lucide-react";
import Logo from "./Logo";
import { useCart } from "@/context/CartContext";

const CATS = [
  ["Gift Boxes", "/shop?category=gift"],
  ["Pizza Boxes", "/shop?category=pizza"],
  ["Cake Boxes", "/shop?category=cake"],
  ["Packaging Boxes", "/shop?category=packaging"],
  ["Corrugated Boxes", "/shop?category=corrugated"],
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [pathname]);

  const goHome = () => {
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("categories")?.scrollIntoView(), 450);
    } else {
      document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-forest/85 backdrop-blur-md border-b border-mint/15" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-home-link">
          <Logo />
          <span className="leading-none">
            <span className="font-display font-semibold tracking-wide text-xl text-kraft">MUBFAR</span>
            <span className="hidden sm:flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] text-mintsoft/80 mt-0.5">
              <MapPin size={9} /> KAMPALA
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 font-mono text-[11px] tracking-[0.18em] uppercase">
          <button onClick={goHome} data-testid="nav-categories-link" className="text-kraft/80 hover:text-mint transition-colors">
            Categories
          </button>
          <Link to="/shop" data-testid="nav-shop-link" className="text-kraft/80 hover:text-mint transition-colors">
            Shop
          </Link>
          <Link to="/faq" data-testid="nav-faq-link" className="text-kraft/80 hover:text-mint transition-colors">
            FAQ
          </Link>
          <Link to="/quote" data-testid="nav-quote-link" className="text-kraft/80 hover:text-mint transition-colors">
            Start a Quote
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart-trigger"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full border border-mint/30 bg-moss/40 hover:bg-pine/40 px-4 h-10 transition-colors"
          >
            <ShoppingBasket size={17} className="text-mint" />
            <span className="hidden sm:block font-mono text-[11px] tracking-widest text-kraft">QUOTE CART</span>
            <AnimatePresence>
              {count > 0 && (
                <motion.span
                  key={count}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-mint text-forest font-mono text-[11px] font-semibold flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <button
            className="md:hidden text-kraft p-2"
            data-testid="mobile-menu-btn"
            onClick={() => setMobile((m) => !m)}
          >
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-forest/95 backdrop-blur-md border-b border-mint/15"
          >
            <div className="px-6 py-5 flex flex-col gap-4 font-mono text-xs tracking-[0.2em] uppercase">
              <Link to="/shop" data-testid="mobile-shop-link">Shop</Link>
              <button onClick={goHome} data-testid="mobile-categories-link" className="text-left">Categories</button>
              <Link to="/faq" data-testid="mobile-faq-link">FAQ</Link>
              <Link to="/quote" data-testid="mobile-quote-link">Start a Quote</Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
